import { useEffect, useState } from 'react';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import {
  useDeleteContractVersionFileMutation,
  useListContractVersionFilesQuery,
  useRouteContractVersionFileMutation,
  useSetContractVersionFileKindMutation,
} from '@shared/api';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { Button } from '@shared/ui/Button';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { Loader } from '@shared/ui/Progress';
import { ScopeNotice } from './ScopeNotice';
import {
  Empty,
  ErrorBanner,
  Header,
  Section,
  SuccessBanner,
  Title,
  ToggleBtn,
} from './styles';
import type { ContractVersionFilesProps } from './types';
import { UploadFilesModal } from './UploadFilesModal';
import { VersionFilesTable } from './VersionFilesTable';

export function ContractVersionFiles({
  contractId,
  versionId,
  versionNo,
}: ContractVersionFilesProps) {
  const { can_write } = useAgentPermission('statut');
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const { data, isLoading } = useListContractVersionFilesQuery({ contractId, versionId });
  const [deleteFile, { isLoading: isDeleting }] = useDeleteContractVersionFileMutation();
  const [setKind, { isLoading: isSettingKind }] = useSetContractVersionFileKindMutation();
  const [routeFile, { isLoading: isRouting }] = useRouteContractVersionFileMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [editingKindId, setEditingKindId] = useState<string | null>(null);
  const [autoApplied, setAutoApplied] = useState(false);

  const items = data?.items ?? [];

  useEffect(() => {
    if (!autoApplied && items.length > 0 && items.length <= 5) {
      setExpanded(true);
      setAutoApplied(true);
    }
  }, [autoApplied, items.length]);

  async function onDownload(fileId: string, filename: string) {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(
      `${base}/agents/statut/contracts/${contractId}/versions/${versionId}/files/${fileId}`,
      { accessToken, fallbackName: filename },
    );
    if (!res.ok) setError(`Не удалось скачать файл (${res.error}).`);
  }

  async function onDelete(fileId: string, filename: string) {
    if (
      !window.confirm(`Удалить вложение «${filename}»? Действие необратимо.`)
    ) {
      return;
    }
    setError(null);
    try {
      await deleteFile({ contractId, versionId, fileId }).unwrap();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось удалить файл. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  async function onChangeKind(fileId: string, documentKind: string) {
    setError(null);
    setSuccess(null);
    try {
      await setKind({ contractId, versionId, fileId, documentKind }).unwrap();
      setSuccess(
        'Тип документа изменён. Чтобы новый состав документов попал в выводы, ' +
          'перезапустите анализ договора.',
      );
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось изменить тип документа. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  async function onRouteFile(fileId: string, filename: string, targetAgent: string, label: string) {
    setError(null);
    setSuccess(null);
    try {
      await routeFile({ contractId, versionId, fileId, target_agent: targetAgent }).unwrap();
      setSuccess(`Приложение «${filename}» передано в модуль «ОСНОВА.${label}».`);
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось передать приложение в отдел.';
      setError(msg);
    }
  }

  return (
    <Section>
      <Header>
        <ToggleBtn
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`contract-files-${versionId}`}
          title={expanded ? 'Свернуть список файлов' : 'Развернуть список файлов'}
        >
          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <Title as="span">
            Документы комплекта · v{versionNo} ({items.length})
          </Title>
        </ToggleBtn>
        <Button
          type="button"
          $size="sm"
          $variant="secondary"
          onClick={() => {
            setSuccess(null);
            setModalOpen(true);
          }}
          disabled={!can_write}
          title={
            !can_write
              ? 'Только чтение'
              : 'Добавит выбранные документы к комплекту и переанализирует весь договор'
          }
        >
          Догрузить документы
        </Button>
      </Header>

      {expanded &&
        (isLoading ? (
          <Loader />
        ) : items.length === 0 ? (
          <Empty>
            Файлы этой версии не сохранены отдельно (старая модель: один файл = одна
            версия). Используйте кнопку «Догрузить документы» — она добавит выбранные
            документы к комплекту и переанализирует весь договор.
          </Empty>
        ) : (
          <div id={`contract-files-${versionId}`}>
            <ScopeNotice items={items} />
            <VersionFilesTable
              items={items}
              canWrite={can_write}
              editingKindId={editingKindId}
              isSettingKind={isSettingKind}
              isDeleting={isDeleting}
              isRouting={isRouting}
              onDownload={onDownload}
              onDelete={onDelete}
              onChangeKind={onChangeKind}
              onRouteFile={onRouteFile}
              onStartEditKind={(id) => {
                setSuccess(null);
                setEditingKindId(id);
              }}
              onCancelEditKind={() => setEditingKindId(null)}
            />
          </div>
        ))}

      {error && <ErrorBanner role="alert">{error}</ErrorBanner>}
      {success && <SuccessBanner role="status">{success}</SuccessBanner>}

      <UploadFilesModal
        open={modalOpen}
        contractId={contractId}
        canWrite={can_write}
        onClose={() => setModalOpen(false)}
        onSuccess={(msg) => setSuccess(msg)}
      />
    </Section>
  );
}
